import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import type { DataKeyEdgeType, EdgeFormProps } from '@/edges';
import {
    AppCheckboxField,
    AppConsiderationTypeMultiSelectField,
    AppEquipmentTypeMultiSelectField,
} from '@/form/FormUI';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { extractFieldErrors } from '@/util/extractFieldErrors';
import { zodResolver } from '@hookform/resolvers/zod';
import { type ConsiderationTypeId, DataKeyDataSchema, type DataKeyDataType, type EquipmentTypeId } from '@rock/types';
import { type FC, useCallback } from 'react';
import { type FieldErrors, useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';
import { considerationTypesForSpaceState, equipmentTypesState } from '../state';

type FormInput = z.input<typeof DataKeyDataSchema>;

const DataKeyEdgeForm: FC<EdgeFormProps<DataKeyEdgeType>> = ({ spaceId, edge, updateEdge, cancelUpdate }) => {
    const equipmentTypes = useAsyncAtomValue(equipmentTypesState(spaceId));
    const considerationTypes = useAsyncAtomValue(considerationTypesForSpaceState(spaceId));
    const form = useForm<FormInput, unknown, DataKeyDataType>({
        resolver: zodResolver(DataKeyDataSchema),
        defaultValues: {
            ...edge.data,
            type: 'dataKey',
        },
    });

    const equipmentItems = useFieldArray({
        control: form.control,
        name: 'items.equipment',
    });

    const selectedEquipmentTypesData = equipmentItems.fields.map((f) => ({
        fieldId: f.id,
        equipmentTypeId: f.equipmentTypeId as EquipmentTypeId,
    }));

    function selectEquipmentType(id: EquipmentTypeId) {
        if (!equipmentItems.fields.some((f) => f.equipmentTypeId === id)) {
            equipmentItems.append({ category: 'equipment', equipmentTypeId: id });
        }
    }

    function removeEquipmentType(index: number) {
        equipmentItems.remove(index);
    }

    const considerationItems = useFieldArray({
        control: form.control,
        name: 'items.considerations',
    });

    const selectedConsiderationTypesData = considerationItems.fields.map((f) => ({
        fieldId: f.id,
        considerationTypeId: f.considerationTypeId as ConsiderationTypeId,
    }));

    function selectConsiderationType(id: ConsiderationTypeId) {
        if (!considerationItems.fields.some((f) => f.considerationTypeId === id)) {
            considerationItems.append({ category: 'consideration', considerationTypeId: id });
        }
    }

    function removeConsiderationType(index: number) {
        considerationItems.remove(index);
    }

    const onSubmit = useCallback(
        (values: DataKeyDataType) => {
            updateEdge({
                ...edge,
                data: values,
            });
        },
        [edge, updateEdge],
    );

    const onInvalid = useCallback((errors: FieldErrors<FormInput>) => {
        toast.error('Validation Errors', {
            description: extractFieldErrors(errors).join(', '),
        });
    }, []);

    const onCancel = useCallback(() => {
        cancelUpdate();
    }, [cancelUpdate]);

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit, onInvalid)} className="flex flex-col gap-8 items-stretch p-5">
                <AppEquipmentTypeMultiSelectField
                    label="Equipment"
                    description="Select one or more equipment."
                    equipmentTypes={equipmentTypes}
                    removeEquipmentType={removeEquipmentType}
                    selectedEquipmentTypesData={selectedEquipmentTypesData}
                    selectEquipmentType={selectEquipmentType}
                />

                <AppConsiderationTypeMultiSelectField
                    label="Considerations"
                    description="Select one or more considerations."
                    considerationTypes={considerationTypes}
                    removeConsiderationType={removeConsiderationType}
                    selectedConsiderationTypesData={selectedConsiderationTypesData}
                    selectConsiderationType={selectConsiderationType}
                />

                <AppCheckboxField
                    control={form.control}
                    name="ignoreForValidation"
                    label="Ignore For Validation"
                    description="Do not consider this edge when checking for loops or exit (use carefully!)"
                />

                <div className="flex flex-row gap-3">
                    <Button type="submit" className="flex-1">
                        Save Details
                    </Button>
                    <Button type="button" className="flex-1" variant="outline" onClick={onCancel}>
                        Cancel
                    </Button>
                </div>
            </form>
        </Form>
    );
};

export default DataKeyEdgeForm;
